import { forwardRef } from '@chakra-ui/react';

import {
  Button,
  ButtonProps,
  OutlineCircleInfoIcon,
  useTheme,
} from '@frontend/components';

type FindingInlineItemVariant = 'pill' | 'text';

type FindingInlineItemProps = {
  variant?: FindingInlineItemVariant;
  isOpen?: boolean;
  isHighlighted?: boolean;
  strikethrough?: boolean;
} & Omit<ButtonProps, 'variant'>;

const pillStyles = {
  size: 'sm' as const,
  buttonVariant: 'secondary' as const,
  pl: 1.25,
  pr: 0.5,
  gap: 0.5,
  iconMy: 0.25,
  hover: {
    bg: 'interaction.surfaceHover',
    color: 'interaction.hover',
    borderColor: 'interaction.hover',
  },
  active: {
    bg: 'interaction.surfaceActive',
    color: 'interaction.active',
    borderColor: 'interaction.active',
  },
};

const textStyles = {
  size: 'md' as const,
  buttonVariant: 'link' as const,
  py: 0,
  pr: 1,
  minHeight: 0,
  gap: 0.25,
  borderRadius: 'md',
  iconMy: 0.5,
  hover: {
    color: 'interaction.hover',
    textDecoration: 'none' as const,
  },
  active: {
    color: 'interaction.active',
  },
};

const highlightedAnimation = {
  zIndex: 4,
  outlineOffset: '0',
  animation: 'finding-target-pulse 1.5s ease-out infinite',
  '@keyframes finding-target-pulse': {
    '0%': {
      outline: '1px solid',
      outlineColor: 'interaction.active',
    },
    '100%': {
      outline: '4px solid',
      outlineColor: 'transparent',
    },
  },
};

/* A component that displays a finding as an inline item, either as a pill or a text button. */
export const FindingInlineItem = forwardRef<FindingInlineItemProps, 'button'>(
  (
    {
      variant = 'pill',
      isOpen,
      isHighlighted,
      strikethrough,
      children,
      ...props
    },
    ref,
  ) => {
    const theme = useTheme();
    const isPill = variant === 'pill';

    return (
      <Button
        className={isPill ? 'finding-pill' : 'finding-text'}
        ref={ref}
        size={isPill ? pillStyles.size : textStyles.size}
        variant={isPill ? pillStyles.buttonVariant : textStyles.buttonVariant}
        borderRadius={
          isPill ? `calc(${theme.sizes[1.5]} * 1)` : textStyles.borderRadius
        }
        pl={isPill ? pillStyles.pl : undefined}
        pr={isPill ? pillStyles.pr : textStyles.pr}
        py={isPill ? undefined : textStyles.py}
        minHeight={isPill ? undefined : textStyles.minHeight}
        gap={isPill ? pillStyles.gap : textStyles.gap}
        textTransform="none"
        color="feature.primary"
        bg={isPill ? 'page.plateLight' : undefined}
        height={isPill ? 'auto' : undefined}
        isActive={isOpen ? true : undefined}
        whiteSpace="normal"
        alignItems="flex-start"
        textAlign="start"
        justifyContent="flex-start"
        textDecoration={isPill && strikethrough ? 'line-through' : undefined}
        _focus={{ shadow: 'none' }}
        _focusVisible={{ shadow: 'focus' }}
        _hover={isPill ? pillStyles.hover : textStyles.hover}
        _active={isPill ? pillStyles.active : textStyles.active}
        sx={{
          '.finding-button-icon': {
            transitionProperty: 'common',
            transitionDuration: 'fast',
            color: 'feature.secondary',
          },
          '&:not([data-active]):hover .finding-button-icon': {
            color: 'interaction.hover',
          },
          '&[data-active] .finding-button-icon': {
            color: 'interaction.active',
          },
          ...(isHighlighted && highlightedAnimation),
        }}
        {...props}
      >
        {children}
        <OutlineCircleInfoIcon
          className="finding-button-icon"
          size={2}
          my={isPill ? pillStyles.iconMy : textStyles.iconMy}
        />
      </Button>
    );
  },
);
FindingInlineItem.displayName = 'FindingInlineItem';
